import { useCallback, useEffect } from 'react';
import { CarTileWrapper } from '../../../../pages/home/hero/car-tile/CarTileWrapper';
import useAppState from '../../../../common/state/appState';
import { shiftUnavailableVehiclesToEnd } from '../../../../pages/home/utilities/helpers';
import { IVehicleTile } from '../../../../types/vehicleTile';
import BaseCarousel from './BaseCarousel';
import { RelatedSearchesTile } from './subcomponents/RelatedSearchesTile';
import CircularProgress from '@mui/material/CircularProgress';

interface ICarouselProps {
    isLoading?: boolean;
    carouselId: string;
    placeholder?: {
        cardText: string;
        buttonText: string;
    };
    vehiclesData: IVehicleTile[];
    isRemoveEnabled?: boolean;
    onRemove?: (vehicle: IVehicleTile) => void;
    nextFocus?: string;
    isInsideHomeBase?: boolean;
    sectionName: string;
    showRelatedSearchesTile?: boolean;
    recommenderCohortId?: string;
    recommenderTestId?: string;
    disableNavButtons?: boolean;
}

export const CarTilesCarousel = ({
    isLoading = false,
    carouselId,
    vehiclesData,
    isRemoveEnabled,
    onRemove,
    nextFocus,
    isInsideHomeBase,
    sectionName,
    showRelatedSearchesTile,
    recommenderCohortId,
    recommenderTestId,
    disableNavButtons,
}: ICarouselProps) => {
    const getFbsDataForStockNumbers = useAppState((state) => state.getFbsDataForStockNumbers);
    const setFavoriteVehicles = useAppState((state) => state.setFavoriteVehicles);

    const sortedVehicleList: IVehicleTile[] = shiftUnavailableVehiclesToEnd(vehiclesData);

    const vehicleCount = vehiclesData?.length;

    const saleableVehicleCount = vehiclesData?.filter((item: IVehicleTile) => item.isSaleable).length;

    const handleRemove = useCallback(
        (vehicle: IVehicleTile) => {
            const remove = vehiclesData.find((v) => v.stockNumber === vehicle.stockNumber);
            if (remove && typeof onRemove === 'function') {
                onRemove(remove);
            }
        },
        [onRemove, vehiclesData],
    );

    const buildCarouselItems = () => {
        const items = sortedVehicleList
            .filter((item) => item !== null && Boolean(item))
            .map((vehicle: IVehicleTile, index: number) => {
                const { stockNumber }: IVehicleTile = vehicle;
                return (
                    <div className="carousel__slide" key={`${stockNumber}-slide-${index}`}>
                        <div className="carousel-slide-item">
                            <CarTileWrapper
                                vehicle={vehicle as IVehicleTile}
                                updatedViewedTiles={handleRemove}
                                enableRemove={isRemoveEnabled ? true : false}
                                allStockNumbers={sortedVehicleList.map((v) => v.stockNumber)}
                                carouselId={carouselId}
                                sectionName={sectionName}
                                recommenderCohortId={recommenderCohortId}
                                recommenderTestId={recommenderTestId}
                            />
                        </div>
                    </div>
                );
            });

        if (showRelatedSearchesTile) {
            items.push(<RelatedSearchesTile key="related-searches-tile" />);
        }

        return items;
    };

    useEffect(() => {
        setFavoriteVehicles();
    }, [setFavoriteVehicles]);

    useEffect(() => {
        getFbsDataForStockNumbers(vehiclesData.map(({ stockNumber }: { stockNumber: number }) => stockNumber));
    }, [getFbsDataForStockNumbers, vehiclesData]);

    if (isLoading) {
        return <CircularProgress color="secondary" size={64} />;
    }

    return (
        <>
            <div
                id={carouselId}
                className="carousel--container"
                data-vehicle-count={vehicleCount}
                data-saleable-vehicle-count={saleableVehicleCount}
            >
                <div className={`carousel--grid ${isInsideHomeBase ? 'is-inside-homebase' : ''}`}>
                    <BaseCarousel
                        name={carouselId}
                        leftAligned={true}
                        nextFocus={`#${nextFocus}`}
                        ariaLabel={carouselId}
                        disableNavButtons={disableNavButtons}
                    >
                        {buildCarouselItems()}
                    </BaseCarousel>
                </div>
            </div>
        </>
    );
};
